import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Fuel, Route, ShieldAlert, Newspaper, ChevronRight, X } from 'lucide-react';
import { SignedIn, SignedOut } from '@clerk/clerk-react';

const CLERK_ENABLED = Boolean(import.meta.env.VITE_CLERK_PUBLISHABLE_KEY);

const CARDS = [
  { Icon: Fuel,        question: 'Where to bunker?',            path: '/bunker'   },
  { Icon: Route,       question: "What's this voyage costing?", path: '/routes'   },
  { Icon: ShieldAlert, question: "What's war risk costing me?", path: '/war-risk' },
  { Icon: Newspaper,   question: "Today's briefing",            path: '/pulse'    },
];

export default function Navbar() {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMenuOpen]);

  useEffect(() => {
    if (!isMenuOpen) return;
    const handler = (e) => { if (e.key === 'Escape') setIsMenuOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isMenuOpen]);

  const close = () => setIsMenuOpen(false);

  const handleCardClick = (path) => {
    close();
    navigate(path);
  };

  return (
    <>
      <nav className="fv-nav">
        <button className="fv-nav-logo" onClick={() => { close(); navigate('/'); }}>
          Fathom<em>Voyage</em>
        </button>
        <button
          className="fv-nav-trigger"
          onClick={() => setIsMenuOpen(o => !o)}
          aria-label="Open navigation menu"
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <rect x="0"  y="0"  width="7" height="7" rx="1" fill="currentColor"/>
            <rect x="11" y="0"  width="7" height="7" rx="1" fill="currentColor"/>
            <rect x="0"  y="11" width="7" height="7" rx="1" fill="currentColor"/>
            <rect x="11" y="11" width="7" height="7" rx="1" fill="currentColor"/>
          </svg>
        </button>
      </nav>

      {isMenuOpen && (
        <>
          <div className="fv-menu-overlay" onClick={close} />
          <div className="fv-menu-panel">
            {/* Panel header */}
            <div className="fv-menu-header">
              <div>
                {CLERK_ENABLED && (
                  <SignedOut>
                    <span style={{ fontSize: 13, color: 'var(--text2)', fontFamily: 'var(--font-sans)' }}>
                      Already a member?{' '}
                      <button
                        onClick={() => { close(); navigate('/sign-in'); }}
                        style={{
                          marginLeft: 6, background: 'none',
                          border: '1px solid var(--border2)', borderRadius: 6,
                          padding: '4px 12px', color: 'var(--text)',
                          fontSize: 13, cursor: 'pointer', fontFamily: 'var(--font-sans)',
                        }}
                      >
                        Sign in
                      </button>
                    </span>
                  </SignedOut>
                )}
              </div>
              <button className="fv-menu-close" onClick={close} aria-label="Close menu">
                <X size={18} strokeWidth={2} />
              </button>
            </div>

            {/* Four navigation cards */}
            <div className="fv-menu-cards">
              {CARDS.map(({ Icon, question, anchor, path }) => (
                <button
                  key={path}
                  className="fv-menu-card"
                  onClick={() => handleCardClick(path)}
                >
                  <div className="fv-menu-card-icon">
                    <Icon size={22} strokeWidth={1.5} />
                  </div>
                  <span className="fv-menu-card-question">{question}</span>
                  <ChevronRight className="fv-menu-card-chevron" size={18} strokeWidth={1.5} />
                </button>
              ))}
            </div>

            {/* Footer */}
            <div className="fv-menu-footer">
              <p style={{ margin: '0 0 8px', fontSize: 11, color: 'var(--text2)', fontFamily: 'var(--font-sans)' }}>
                Free — no card required
              </p>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center', alignItems: 'center' }}>
                <button
                  onClick={() => { close(); navigate('/privacy'); }}
                  style={{ background: 'none', border: 'none', fontSize: 11, color: 'var(--text2)', cursor: 'pointer', fontFamily: 'var(--font-sans)', padding: 0 }}
                >
                  Privacy
                </button>
                <span style={{ color: 'var(--border2)', fontSize: 11 }}>·</span>
                <button
                  onClick={() => { close(); navigate('/terms'); }}
                  style={{ background: 'none', border: 'none', fontSize: 11, color: 'var(--text2)', cursor: 'pointer', fontFamily: 'var(--font-sans)', padding: 0 }}
                >
                  Terms
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
